import { useState } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { opsApi } from "../lib/api";

/** Status moves are the spine of the project, so each one is a deliberate act with a reason. */
export default function StatusDialog({ project, initialTarget = "", onClose, onDone }) {
  const [target, setTarget] = useState(initialTarget);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [warning, setWarning] = useState(null);

  const row = (project.status.transitions || []).find((t) => t.status === target);

  const submit = async (confirm = false) => {
    setBusy(true);
    try {
      const res = await opsApi.setStatus(project.id, { status: target, reason, confirm });
      toast.success(`Job is now ${res.status.replace("_", " ")}`);
      onDone(res.drafts);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.confirmable) {
        setWarning(d.detail);
      } else if (d?.missing) {
        toast.error(`${d.detail}: ${d.missing.join(", ")}`);
      } else {
        toast.error(typeof d === "string" ? d : "That move was refused");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
         onClick={onClose}>
      <div className="w-full max-w-md rounded-xl bg-white p-5 shadow-xl"
           onClick={(e) => e.stopPropagation()} data-testid="ops-status-dialog">
        <h3 className="text-base font-semibold text-slate-900">Change status</h3>
        <p className="mt-1 text-xs text-slate-500">
          Currently {project.status.label} — {project.status.meaning}
        </p>

        <div className="mt-4 space-y-2">
          {(project.status.transitions || []).length === 0 && (
            <p className="text-sm text-slate-500">This job is finished — there is nowhere left to move it.</p>
          )}
          {(project.status.transitions || []).map((t) => (
            <button key={t.status} disabled={!t.allowed} onClick={() => { setTarget(t.status); setWarning(null); }}
                    data-testid={`ops-status-option-${t.status}`}
                    className={`w-full rounded-lg border px-3 py-2 text-left text-sm transition-colors ${
                      target === t.status ? "border-slate-900 bg-slate-50"
                        : t.allowed ? "border-slate-200 hover:border-slate-400"
                          : "cursor-not-allowed border-slate-100 bg-slate-50 text-slate-400"}`}>
              <span className="font-medium">{t.label}</span>
              {!t.allowed && <span className="ml-2 text-xs">only {t.who}</span>}
              {t.reason_required && t.allowed && <span className="ml-2 text-xs text-slate-400">reason required</span>}
            </button>
          ))}
        </div>

        {row?.reason_required && (
          <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3}
                    placeholder="Why is it moving?" className="mt-3 text-sm"
                    data-testid="ops-status-reason" />
        )}

        {warning && (
          <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800"
               data-testid="ops-status-warning">
            {warning}. Opening anyway is recorded against your name.
          </div>
        )}

        <div className="mt-4 flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onClose} data-testid="ops-status-cancel">Cancel</Button>
          <Button size="sm" disabled={!target || busy} onClick={() => submit(!!warning)}
                  data-testid="ops-status-confirm">
            {busy && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
            {warning ? "Open anyway" : "Confirm"}
          </Button>
        </div>
      </div>
    </div>
  );
}
